import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useCustomer } from "~/context/User/Customer";
import { RouteParams } from "~/models";
import { PagerDutyBusinessService } from "~/models/pagerDutyBusinessService";
import { generateActionSuccessText, useAxios } from "~/tools";
import { ToolsAndIntegrations as ToolsAndIntegrationsType } from "../BasicCustomerInfo/types";
import { validateDate, tenantOptions } from "./constants";

type EditPagerDutyBusinessServiceProps = {
  businessService: PagerDutyBusinessService;
  refetch: () => void;
  fetchCustomer: () => void;
  disabled?: boolean;
};

type FormValues = {
  tenant: "PD US" | "PD EU";
  reportingStartDate: string;
  reportingEndDate: string;
  name: string;
};

function EditPagerDutyBusinessService({
  businessService,
  refetch,
  fetchCustomer,
  disabled,
}: EditPagerDutyBusinessServiceProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const initialValues = {
    tenant: businessService.tenant || "PD US",
    reportingStartDate: businessService.reportingStartDate || "",
    reportingEndDate: businessService.reportingEndDate || "",
    name: businessService.name || "",
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
  });

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();

  const state = useParams<RouteParams>();
  const { customerId } = state;

  const { customer } = useCustomer();

  const hasChanges = React.useMemo(() => {
    const originalStartDate = businessService.reportingStartDate || "";
    const originalEndDate = businessService.reportingEndDate || "";

    return (
      watcher.reportingStartDate !== originalStartDate ||
      watcher.reportingEndDate !== originalEndDate
    );
  }, [
    watcher.reportingStartDate,
    watcher.reportingEndDate,
    businessService.reportingStartDate,
    businessService.reportingEndDate,
  ]);

  const onSubmit = async (values: FormValues) => {
    try {
      const updatedBusinessService: PagerDutyBusinessService = {
        id: businessService.id, // Keep original id (lowercase)
        tenant: businessService.tenant, // Keep original tenant
        reportingStartDate: values.reportingStartDate || null,
        reportingEndDate: values.reportingEndDate || null,
        name: businessService.name, // Keep original name (for display)
      };

      const updateData = {
        ...customer,
      };

      const tools =
        updateData.toolsAndIntegrations ?? ({} as ToolsAndIntegrationsType);
      updateData.toolsAndIntegrations = tools;

      const existingServices: PagerDutyBusinessService[] = Array.isArray(
        tools.pagerDutyCustomers
      )
        ? tools.pagerDutyCustomers
        : [];

      tools.pagerDutyCustomers = existingServices.map((p) =>
        p.id === businessService.id ? { ...updatedBusinessService } : { ...p }
      );

      await Axios().put(`/customers/${customerId}`, updateData);
      fetchCustomer();
      refetch();

      showSuccess(
        generateActionSuccessText(`PagerDuty Business Service`)()(`updated`)()
      );
      setTimeout(() => {
        setIsSidebarOpen(false);
      }, 200);
    } catch (e: any) {
      showError(e.response);
    }
  };

  const businessServiceOptions = [
    { value: businessService.name, label: businessService.name },
  ];

  const handleSidebarOpen = () => {
    methods.reset({
      tenant: businessService.tenant || "PD US",
      reportingStartDate: businessService.reportingStartDate || "",
      reportingEndDate: businessService.reportingEndDate || "",
      name: businessService.name || "",
    });
    setIsSidebarOpen(true);
  };

  return (
    <>
      <Button
        icon="edit"
        size="sm"
        severity="low"
        disabled={disabled}
        onClick={handleSidebarOpen}
      >
        Edit
      </Button>

      <SidebarForm
        title="Edit PagerDuty Business Service"
        width="36rem"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods as any}
        submit={onSubmit}
      >
        <SidebarDropdown
          name="tenant"
          label="Tenant"
          options={tenantOptions}
          defaultValue={businessService.tenant || "PD US"}
          key="tenant"
          disabled
          required
        />

        <SidebarDropdown
          name="name"
          label="Business Service"
          options={businessServiceOptions}
          defaultValue={businessService.name}
          key="business-service"
          disabled
          required
        />

        <SidebarTextField
          name="reportingStartDate"
          label="Reporting Start Date"
          placeholder="YYYY-MM-DD"
          defaultValue={businessService.reportingStartDate || ""}
          registerOptions={{ validate: validateDate }}
        />

        <SidebarTextField
          name="reportingEndDate"
          label="Reporting End Date"
          placeholder="YYYY-MM-DD"
          defaultValue={businessService.reportingEndDate || ""}
          registerOptions={{ validate: validateDate }}
        />

        <SidebarFormButtons
          submitText="Update"
          submitEnabled={hasChanges}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { EditPagerDutyBusinessService };
